import type {
    Bi,
    Cell,
    Lakara,
    Pada,
    Purusha,
    Vacana,
    VerbTable
} from '@/sanskrit/lib/sanskrit/types'

import { PURUSHAS, VACANAS } from '@/sanskrit/lib/sanskrit/types'

/**
 * How an ending meets a thematic stem that ends in अ.
 * Each join is a real sandhi operation with its own rule, never plain string pasting.
 */
export type JoinKind =
    | 'plain'
    | 'merge'
    | 'lengthen'
    | 'savarna'
    | 'zero'
    | 'guna-e'
    | 'para-e'
    | 'vrddhi-ai'

export interface EndingSpec {
    show: string
    join: JoinKind
}

export type EndingTable = Record<Purusha, Record<Vacana, EndingSpec>>

const e = (show: string, join: JoinKind = 'plain'): EndingSpec => ({ show, join })

export const ENDINGS: Record<'lat' | 'lan' | 'lot' | 'vidhilin', Record<Pada, EndingTable>> = {
    lat: {
        P: {
            prathama: { eka: e('ति'), dvi: e('तः'), bahu: e('अन्ति', 'merge') },
            madhyama: { eka: e('सि'), dvi: e('थः'), bahu: e('थ') },
            uttama: { eka: e('मि', 'lengthen'), dvi: e('वः', 'lengthen'), bahu: e('मः', 'lengthen') }
        },
        A: {
            prathama: { eka: e('ते'), dvi: e('इते', 'guna-e'), bahu: e('अन्ते', 'merge') },
            madhyama: { eka: e('से'), dvi: e('इथे', 'guna-e'), bahu: e('ध्वे') },
            uttama: { eka: e('ए', 'para-e'), dvi: e('वहे', 'lengthen'), bahu: e('महे', 'lengthen') }
        }
    },
    lan: {
        P: {
            prathama: { eka: e('त्'), dvi: e('ताम्'), bahu: e('अन्', 'merge') },
            madhyama: { eka: e('ः'), dvi: e('तम्'), bahu: e('त') },
            uttama: { eka: e('अम्', 'merge'), dvi: e('व', 'lengthen'), bahu: e('म', 'lengthen') }
        },
        A: {
            prathama: { eka: e('त'), dvi: e('इताम्', 'guna-e'), bahu: e('अन्त', 'merge') },
            madhyama: { eka: e('थाः'), dvi: e('इथाम्', 'guna-e'), bahu: e('ध्वम्') },
            uttama: { eka: e('इ', 'guna-e'), dvi: e('वहि', 'lengthen'), bahu: e('महि', 'lengthen') }
        }
    },
    lot: {
        P: {
            prathama: { eka: e('तु'), dvi: e('ताम्'), bahu: e('अन्तु', 'merge') },
            madhyama: { eka: e('हि', 'zero'), dvi: e('तम्'), bahu: e('त') },
            uttama: { eka: e('आनि', 'savarna'), dvi: e('आव', 'savarna'), bahu: e('आम', 'savarna') }
        },
        A: {
            prathama: { eka: e('ताम्'), dvi: e('इताम्', 'guna-e'), bahu: e('अन्ताम्', 'merge') },
            madhyama: { eka: e('स्व'), dvi: e('इथाम्', 'guna-e'), bahu: e('ध्वम्') },
            uttama: { eka: e('ऐ', 'vrddhi-ai'), dvi: e('आवहै', 'savarna'), bahu: e('आमहै', 'savarna') }
        }
    },
    vidhilin: {
        P: {
            prathama: { eka: e('ईत्', 'guna-e'), dvi: e('ईताम्', 'guna-e'), bahu: e('ईयुः', 'guna-e') },
            madhyama: { eka: e('ईः', 'guna-e'), dvi: e('ईतम्', 'guna-e'), bahu: e('ईत', 'guna-e') },
            uttama: { eka: e('ईयम्', 'guna-e'), dvi: e('ईव', 'guna-e'), bahu: e('ईम', 'guna-e') }
        },
        A: {
            prathama: { eka: e('ईत', 'guna-e'), dvi: e('ईयाताम्', 'guna-e'), bahu: e('ईरन्', 'guna-e') },
            madhyama: { eka: e('ईथाः', 'guna-e'), dvi: e('ईयाथाम्', 'guna-e'), bahu: e('ईध्वम्', 'guna-e') },
            uttama: { eka: e('ईय', 'guna-e'), dvi: e('ईवहि', 'guna-e'), bahu: e('ईमहि', 'guna-e') }
        }
    }
}

const AA = 'ा'
const E_SIGN = 'े'
const AI_SIGN = 'ै'

/** Attach an ending to a stem that ends in an inherent अ, applying the join's sandhi. */
export const attach = (stem: string, spec: EndingSpec): string => {
    const s = spec.show

    switch (spec.join) {
        case 'plain':
            return stem + s
        case 'merge':
            return stem + s.slice(1)
        case 'lengthen':
            return stem + AA + s
        case 'savarna':
            return stem + AA + s.slice(1)
        case 'zero':
            return stem
        case 'guna-e':
            return stem + E_SIGN + s.slice(1)
        case 'para-e':
            return stem + E_SIGN + s.slice(1)
        case 'vrddhi-ai':
            return stem + AI_SIGN + s.slice(1)
    }
}

export interface JoinRule {
    name: Bi
    sutra?: string
    text: Bi
}

export const JOIN_RULES: Record<JoinKind, JoinRule | null> = {
    plain: null,
    merge: {
        name: { hi: 'पररूप सन्धि', en: 'Pararūpa (the second vowel wins)' },
        sutra: 'अतो गुणे (6.1.97)',
        text: {
            hi: 'अङ्ग का अन्तिम अ और प्रत्यय का अ मिलकर एक ही अ बनते हैं।',
            en: 'The stem’s final अ and the ending’s अ merge into a single अ.'
        }
    },
    lengthen: {
        name: { hi: 'दीर्घ (अ → आ)', en: 'Lengthening (अ → आ)' },
        sutra: 'अतो दीर्घो यञि (7.3.101)',
        text: {
            hi: 'म् या व् से आरम्भ होने वाले प्रत्यय से पहले अङ्ग का अ दीर्घ आ बन जाता है। इसीलिए पठमि नहीं, पठामि।',
            en: 'Before an ending that begins with म् or व्, the stem’s अ becomes long आ. That is why it is पठामि, never पठमि.'
        }
    },
    savarna: {
        name: { hi: 'सवर्णदीर्घ सन्धि', en: 'Savarṇa-dīrgha (like vowels merge long)' },
        sutra: 'अकः सवर्णे दीर्घः (6.1.101)',
        text: {
            hi: 'अ + आ मिलकर आ बनते हैं।',
            en: 'अ + आ combine into a single long आ.'
        }
    },
    zero: {
        name: { hi: 'प्रत्यय का लोप', en: 'Ending dropped' },
        sutra: 'अतो हेः (6.4.105)',
        text: {
            hi: 'अ से समाप्त अङ्ग के बाद लोट् मध्यम पुरुष एकवचन का "हि" लुप्त हो जाता है, इसलिए केवल अङ्ग बचता है: पठ!',
            en: 'After a stem in अ, the imperative ending हि disappears, so the bare stem is the command: पठ!'
        }
    },
    'guna-e': {
        name: { hi: 'गुण सन्धि (अ + इ/ई = ए)', en: 'Guṇa (अ + इ/ई = ए)' },
        sutra: 'आद्गुणः (6.1.87)',
        text: {
            hi: 'अङ्ग का अ और प्रत्यय का इ/ई मिलकर ए बनते हैं।',
            en: 'The stem’s अ and the ending’s इ/ई combine into ए.'
        }
    },
    'para-e': {
        name: { hi: 'पररूप (अ + ए = ए)', en: 'Pararūpa (अ + ए = ए)' },
        sutra: 'अतो गुणे (6.1.97)',
        text: {
            hi: 'अङ्ग का अ और प्रत्यय का ए मिलकर ए ही रहता है।',
            en: 'The stem’s अ followed by ए gives just ए.'
        }
    },
    'vrddhi-ai': {
        name: { hi: 'वृद्धि सन्धि (अ + ऐ = ऐ)', en: 'Vṛddhi (अ + ऐ = ऐ)' },
        sutra: 'वृद्धिरेचि (6.1.88)',
        text: {
            hi: 'अ और ऐ मिलकर ऐ बनते हैं।',
            en: 'अ and ऐ combine into ऐ.'
        }
    }
}

export const buildTable = (fn: (p: Purusha, v: Vacana) => Cell): VerbTable => {
    const table = {} as VerbTable

    for (const p of PURUSHAS) {
        table[p] = {} as Record<Vacana, Cell>
        for (const v of VACANAS) table[p][v] = fn(p, v)
    }

    return table
}

/**
 * Write a table compactly: nine cells in order
 * (प्रथम एक, द्वि, बहु, मध्यम एक, द्वि, बहु, उत्तम एक, द्वि, बहु).
 * Accepted variants are separated with "/".
 */
export const tbl = (...cells: string[]): VerbTable => {
    if (cells.length !== 9) throw new Error('A verb table needs exactly 9 cells')

    return buildTable((p, v) => {
        const index = PURUSHAS.indexOf(p) * 3 + VACANAS.indexOf(v)
        return cells[index].split('/')
    })
}

export type ThematicLakara = 'lat' | 'lan' | 'lot' | 'vidhilin'

/** Generate a thematic paradigm from one or more stems (several stems give accepted variants). */
export const thematicTable = (stems: string[], lakara: ThematicLakara, pada: Pada): VerbTable => {
    const endings = ENDINGS[lakara][pada]

    return buildTable((p, v) => {
        const forms = stems.map(stem => attach(stem, endings[p][v]))

        if (lakara === 'lot' && pada === 'P' && v === 'eka' && (p === 'prathama' || p === 'madhyama')) {
            for (const stem of stems) forms.push(stem + 'तात्')
        }

        return Array.from(new Set(forms))
    })
}

/** लुट्: the agent-noun base (पठित) + forms of अस्. */
export const lutTable = (bases: string[], pada: Pada): VerbTable => {
    const P = ['ा', 'ारौ', 'ारः', 'ासि', 'ास्थः', 'ास्थ', 'ास्मि', 'ास्वः', 'ास्मः']
    const A = ['ा', 'ारौ', 'ारः', 'ासे', 'ासाथे', 'ाध्वे', 'ाहे', 'ास्वहे', 'ास्महे']
    const endings = pada === 'P' ? P : A

    return buildTable((p, v) => {
        const index = PURUSHAS.indexOf(p) * 3 + VACANAS.indexOf(v)
        return bases.map(base => base + endings[index])
    })
}

export const cellOf = (table: VerbTable, p: Purusha, v: Vacana): Cell => table[p][v]

export const primary = (cell: Cell): string => cell[0]

export const tableForms = (table: VerbTable): string[] => {
    const out: string[] = []
    for (const p of PURUSHAS) for (const v of VACANAS) out.push(...table[p][v])
    return out
}

export const LAKARA_STEM_KIND: Record<Lakara, 'present' | 'future' | 'other'> = {
    lat: 'present',
    lan: 'present',
    lot: 'present',
    vidhilin: 'present',
    lrt: 'future',
    lrn: 'future',
    lut: 'other',
    lit: 'other',
    lun: 'other',
    ashirlin: 'other',
    let: 'other'
}

/** Which basic ending table a lakara borrows (लृट् uses लट् endings, लृङ् uses लङ्). */
export const ENDING_SOURCE: Partial<Record<Lakara, ThematicLakara>> = {
    lat: 'lat',
    lan: 'lan',
    lot: 'lot',
    vidhilin: 'vidhilin',
    lrt: 'lat',
    lrn: 'lan'
}
